import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  CircleAlert,
  FileCheck2,
  Gavel,
  Loader2,
  Scale,
  SearchCheck,
  Share2,
  Sparkles,
} from 'lucide-react';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../auth/firebaseConfig';
import { EvictionRules2026Report } from '../../types/evictionRules2026';
import PseoEmailCapture from '../../components/pseo/PseoEmailCapture';
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  LinkedinIcon,
} from 'react-share';

const EvictionRightsDetailPage: React.FC = () => {
  const { rightsSlug } = useParams<{ rightsSlug: string }>();
  const [data, setData] = useState<EvictionRules2026Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const slug = useMemo(() => {
    const raw = String(rightsSlug || '').trim().toLowerCase();
    if (!raw) return '';
    if (raw.startsWith('eviction-rules-') && raw.endsWith('-2026')) return raw;
    return raw;
  }, [rightsSlug]);

  useEffect(() => {
    if (!slug) {
      setError('Rights page slug is missing.');
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(
          `${CLOUD_FUNCTIONS_BASE_URL}/getEvictionRulesReport?slug=${encodeURIComponent(slug)}`
        );
        const result = await response.json();
        if (!result.success || !result.data) {
          setError('Rights report not found.');
          return;
        }
        setData(result.data as EvictionRules2026Report);
      } catch (e) {
        setError('Unable to load rights report.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center bg-slate-50">
        <Loader2 className="h-10 w-10 animate-spin text-emerald-600 mb-3" />
        <p className="font-bold text-slate-500">Loading rights report...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-2xl mx-auto my-20 px-4">
        <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center">
          <CircleAlert className="h-16 w-16 text-amber-500 mx-auto mb-6" />
          <h1 className="text-3xl font-black text-slate-900 mb-3">Report Unavailable</h1>
          <p className="text-slate-600 mb-8">{error}</p>
          <Link
            to="/rights"
            className="inline-block rounded-2xl bg-emerald-600 text-white px-8 py-4 font-black hover:bg-emerald-700 no-underline"
          >
            Open Rights Hub
          </Link>
        </div>
      </div>
    );
  }

  const canonicalUrl = `https://rentsmart.ie/rights/${data.slug}`;
  const shareUrl = typeof window !== 'undefined' ? window.location.href : canonicalUrl;
  const shareText = `Can I be evicted in ${data.location_name} in 2026? Check this RentSmart.ie rights guide with landlord tier rules, notice periods, and required legal documents.`;

  return (
    <div className="relative min-h-screen w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-x-hidden bg-slate-50">
      <Helmet>
        <title>{data.seo_content.meta_title}</title>
        <meta name="description" content={data.seo_content.meta_desc} />
        <meta name="keywords" content={data.seo_content.keywords.join(', ')} />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(data.schema)}</script>
      </Helmet>

      <header className="bg-slate-900 text-white pt-10 pb-16 px-4 relative overflow-hidden">
        <div className="absolute -top-20 -right-16 h-64 w-64 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="absolute -bottom-20 -left-16 h-64 w-64 rounded-full bg-sky-500/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-black uppercase tracking-widest mb-4">
            <AlertTriangle className="h-4 w-4" /> 2026 Eviction Rules
          </div>
          <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-3">{data.seo_content.h1}</h1>
          <p className="text-slate-300 text-lg max-w-3xl leading-relaxed">
            Rights breakdown for {data.location_name}: landlord tier logic, notice periods, and the
            statutory declaration validator.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span className="text-xs font-black uppercase tracking-widest text-slate-400">Share:</span>
            <FacebookShareButton url={shareUrl} hashtag="#RentSmartIE">
              <FacebookIcon size={30} round />
            </FacebookShareButton>
            <TwitterShareButton url={shareUrl} title={shareText}>
              <TwitterIcon size={30} round />
            </TwitterShareButton>
            <WhatsappShareButton url={shareUrl} title={shareText}>
              <WhatsappIcon size={30} round />
            </WhatsappShareButton>
            <LinkedinShareButton
              url={shareUrl}
              title={`${data.location_name} Eviction Rules 2026`}
              summary={shareText}
              source="RentSmart.ie"
            >
              <LinkedinIcon size={30} round />
            </LinkedinShareButton>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 -mt-8 pb-16 break-words">
        <section className="grid lg:grid-cols-12 gap-8 mb-8 mt-16">
          <div className="lg:col-span-8 space-y-8">
            <div className="rounded-[2.5rem] border border-slate-100 bg-white p-5 sm:p-6 md:p-10 shadow-sm">
              <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-2">
                <Scale className="h-5 w-5 text-emerald-600" /> Landlord Tier Checker
              </h2>
              <div className="grid md:grid-cols-2 gap-4 mb-5">
                <div className="rounded-2xl border border-sky-200 bg-sky-50 p-5">
                  <p className="text-[10px] uppercase tracking-widest font-black text-sky-700 mb-2">
                    Tier 1: Small Landlord
                  </p>
                  <p className="text-sm text-sky-900 font-semibold leading-relaxed">
                    {data.landlord_tier_checker.tier_1_small}
                  </p>
                </div>
                <div className="rounded-2xl border border-violet-200 bg-violet-50 p-5">
                  <p className="text-[10px] uppercase tracking-widest font-black text-violet-700 mb-2">
                    Tier 2: Large Landlord/Company
                  </p>
                  <p className="text-sm text-violet-900 font-semibold leading-relaxed">
                    {data.landlord_tier_checker.tier_2_large}
                  </p>
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 mb-4">
                <p className="text-sm text-slate-700 font-semibold leading-relaxed">
                  {data.landlord_tier_checker.local_context}
                </p>
              </div>
              <div className="space-y-2">
                {data.landlord_tier_checker.checklist.map((item, index) => (
                  <div key={`${item}-${index}`} className="rounded-xl border border-slate-200 bg-white p-4 text-sm font-semibold text-slate-700">
                    {index + 1}. {item}
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-[2.5rem] border border-slate-100 bg-white p-5 sm:p-6 md:p-10 shadow-sm">
              <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-2">
                <SearchCheck className="h-5 w-5 text-sky-600" /> Notice Period Calculator Table
              </h2>
              <div className="md:hidden space-y-2">
                {data.notice_period_calculator.table.map((row) => (
                  <div
                    key={row.tenancy_length}
                    className="rounded-xl border border-slate-200 bg-slate-50 p-4"
                  >
                    <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">
                      Length of Tenancy
                    </p>
                    <p className="text-sm font-bold text-slate-800 mb-2">{row.tenancy_length}</p>
                    <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">
                      Required Notice Period
                    </p>
                    <p className="text-sm font-black text-slate-900">{row.notice_period_days} days</p>
                  </div>
                ))}
              </div>
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 text-xs uppercase tracking-wider text-slate-500">
                        Length of Tenancy
                      </th>
                      <th className="text-left py-3 text-xs uppercase tracking-wider text-slate-500">
                        Required Notice Period
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.notice_period_calculator.table.map((row) => (
                      <tr key={row.tenancy_length} className="border-b border-slate-100">
                        <td className="py-3 text-sm font-bold text-slate-800">{row.tenancy_length}</td>
                        <td className="py-3 text-sm font-black text-slate-900">{row.notice_period_days} days</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900 font-semibold leading-relaxed">
                {data.notice_period_calculator.rent_cap_status}
              </div>
            </div>

            <div className="rounded-[2.5rem] border border-slate-100 bg-white p-5 sm:p-6 md:p-10 shadow-sm">
              <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-2">
                <FileCheck2 className="h-5 w-5 text-emerald-600" /> Statutory Declaration Validator
              </h2>
              <div className="grid md:grid-cols-2 gap-5">
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
                  <p className="text-[10px] uppercase tracking-widest font-black text-emerald-700 mb-3">
                    Required Documents
                  </p>
                  <ul className="space-y-2 text-sm text-emerald-900 font-semibold">
                    {data.statutory_declaration_validator.required_documents.map((item, index) => (
                      <li key={`${item}-${index}`}>• {item}</li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-2xl border border-rose-200 bg-rose-50 p-5">
                  <p className="text-[10px] uppercase tracking-widest font-black text-rose-700 mb-3">
                    Failure Flags
                  </p>
                  <ul className="space-y-2 text-sm text-rose-900 font-semibold">
                    {data.statutory_declaration_validator.failure_flags.map((item, index) => (
                      <li key={`${item}-${index}`}>• {item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {data.ai_enrichment ? (
              <div className="rounded-[2.5rem] border border-slate-100 bg-white p-5 sm:p-6 md:p-10 shadow-sm">
                <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-emerald-600" /> Crisis-State Guidance
                </h2>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 mb-4">
                  <p className="text-sm text-slate-700 font-semibold leading-relaxed">
                    {data.ai_enrichment.crisis_triage}
                  </p>
                </div>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
                    <p className="text-[10px] uppercase tracking-widest font-black text-emerald-700 mb-2">
                      Tenant Action Plan
                    </p>
                    <ul className="space-y-2 text-sm text-emerald-900 font-semibold">
                      {data.ai_enrichment.tenant_action_plan.map((item, index) => (
                        <li key={`${item}-${index}`}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-2xl border border-sky-200 bg-sky-50 p-5">
                    <p className="text-[10px] uppercase tracking-widest font-black text-sky-700 mb-2">
                      Landlord Compliance Playbook
                    </p>
                    <ul className="space-y-2 text-sm text-sky-900 font-semibold">
                      {data.ai_enrichment.landlord_compliance_playbook.map((item, index) => (
                        <li key={`${item}-${index}`}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 mb-4">
                  <p className="text-sm text-amber-900 font-semibold leading-relaxed">
                    {data.ai_enrichment.county_risk_commentary}
                  </p>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="rounded-2xl border border-slate-200 bg-white p-5">
                    <p className="text-[10px] uppercase tracking-widest font-black text-slate-600 mb-2">
                      Evidence Pack Tips
                    </p>
                    <ul className="space-y-2 text-sm text-slate-700 font-semibold">
                      {data.ai_enrichment.evidence_pack_tips.map((item, index) => (
                        <li key={`${item}-${index}`}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-5">
                    <p className="text-[10px] uppercase tracking-widest font-black text-slate-600 mb-2">
                      Mediation Path
                    </p>
                    <ul className="space-y-2 text-sm text-slate-700 font-semibold">
                      {data.ai_enrichment.mediation_path.map((item, index) => (
                        <li key={`${item}-${index}`}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ) : null}

            <PseoEmailCapture
              pageType="eviction-rights"
              pageSlug={data.slug}
              locationName={data.location_name}
              contextHeadline={`Get Rights & Market Updates For ${data.location_name}`}
              contextReason={`Stay informed with verified Irish rental-market and rights updates for ${data.location_name}. We tailor briefings by user type so renters, landlords, and agents receive relevant legal-context changes.`}
            />

            <div className="rounded-[2.5rem] border border-slate-100 bg-white p-5 sm:p-6 md:p-10 shadow-sm">
              <h2 className="text-2xl font-black text-slate-900 mb-4">FAQ</h2>
              <div className="space-y-3">
                {data.seo_content.faq.map((faq, index) => (
                  <details key={`${faq.question}-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <summary className="cursor-pointer font-bold text-slate-800">{faq.question}</summary>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">{faq.answer}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-8 space-y-6">
              <div className="rounded-3xl bg-gradient-to-br from-emerald-700 to-emerald-900 text-white p-6 shadow-xl shadow-emerald-900/20">
                <h3 className="text-xl font-black mb-2">Immediate Next Step</h3>
                <p className="text-emerald-100/90 text-sm mb-5 leading-relaxed">
                  If you need to move quickly, start a fresh rental search with county filtering.
                </p>
                <a
                  href={data.ctas.rent.path}
                  className="no-underline flex items-start gap-2 justify-between rounded-xl bg-white text-emerald-800 px-4 py-3 font-black text-sm hover:bg-emerald-50 transition-all"
                >
                  <span className="break-words">{data.ctas.rent.label}</span>
                  <ArrowRight className="h-4 w-4 shrink-0 mt-0.5" />
                </a>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-6">
                <h3 className="text-xl font-black text-slate-900 mb-3 flex items-center gap-2">
                  <Gavel className="h-5 w-5 text-sky-600" /> Local Legal Aid Directory
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <p className="font-black text-slate-900 mb-1">
                      {data.local_help.legal_aid_directory.threshold.office}
                    </p>
                    <p className="text-slate-700">{data.local_help.legal_aid_directory.threshold.address}</p>
                    <p className="text-slate-700">{data.local_help.legal_aid_directory.threshold.phone}</p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <p className="font-black text-slate-900 mb-1">
                      {data.local_help.legal_aid_directory.flac.center}
                    </p>
                    <p className="text-slate-700">{data.local_help.legal_aid_directory.flac.address}</p>
                    <p className="text-slate-700">{data.local_help.legal_aid_directory.flac.phone}</p>
                  </div>
                </div>
              </div>

              {data.ctas.related.length ? (
                <div className="rounded-3xl border border-slate-200 bg-white p-6">
                  <h3 className="text-xl font-black text-slate-900 mb-3">Related County Rights Pages</h3>
                  <div className="space-y-2">
                    {data.ctas.related.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="no-underline flex items-start gap-2 justify-between rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold text-slate-700 hover:border-emerald-300 hover:text-emerald-700"
                      >
                        <span className="break-words">{item.label}</span>
                        <ArrowRight className="h-4 w-4 shrink-0 mt-0.5" />
                      </Link>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-white to-emerald-50 p-6">
                <h3 className="text-xl font-black text-slate-900 mb-2 flex items-center gap-2">
                  <Share2 className="h-5 w-5 text-emerald-600" /> Share RentSmart.ie
                </h3>
                <p className="text-sm text-slate-600 font-medium mb-4 leading-relaxed">
                  Help more renters and landlords find this county rights guide on RentSmart.ie.
                </p>
                <div className="flex items-center gap-2">
                  <FacebookShareButton url={shareUrl} hashtag="#RentSmartIE">
                    <FacebookIcon size={32} round />
                  </FacebookShareButton>
                  <TwitterShareButton url={shareUrl} title={shareText}>
                    <TwitterIcon size={32} round />
                  </TwitterShareButton>
                  <WhatsappShareButton url={shareUrl} title={shareText}>
                    <WhatsappIcon size={32} round />
                  </WhatsappShareButton>
                  <LinkedinShareButton
                    url={shareUrl}
                    title={`${data.location_name} Eviction Rules 2026`}
                    summary={shareText}
                    source="RentSmart.ie"
                  >
                    <LinkedinIcon size={32} round />
                  </LinkedinShareButton>
                </div>
              </div>
            </div>
          </aside>
        </section>

        <section className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5" />
            <p className="text-sm text-amber-900/90 font-semibold leading-relaxed">
              This page is informational guidance only and not legal advice. Validate notices and
              timelines directly with RTB guidance and qualified legal support.
            </p>
          </div>
        </section>
      </main>
    </div>
  );
};

export default EvictionRightsDetailPage;
